import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { useI18n } from '../../../i18n'
import type { AdminAccount, AdminProfile } from '../../../types'
import { BackofficeApiError } from '../../../lib/api/backoffice/http'
import { DEFAULT_PROFILE } from '../constants'
import { requestProfileSnapshot, requestProfileUpdate } from '../profileApi'
import { mergeProfile, mergeWithCurrentProfile } from './profileMappers'
import type { AdminHeadersGetter } from './types'

type UseProfileDataActionsParams = {
  token: string | null
  authHeaders: AdminHeadersGetter
  t: ReturnType<typeof useI18n>['t']
  setProfile: Dispatch<SetStateAction<AdminProfile>>
  setAccount: Dispatch<SetStateAction<AdminAccount | null>>
  ensureAuthenticated: () => boolean
}

export const useProfileDataActions = ({
  token,
  authHeaders,
  t,
  setProfile,
  setAccount,
  ensureAuthenticated,
}: UseProfileDataActionsParams) => {
  const [profileLoading, setProfileLoading] = useState(false)

  const applyProfileState = useCallback(
    (data?: Partial<AdminProfile>) => {
      setProfile(mergeProfile(data))
    },
    [setProfile],
  )

  const hydrateProfile = useCallback(
    (data?: Partial<AdminProfile>, accountInfo?: AdminAccount | null) => {
      if (data) {
        applyProfileState(data)
      }
      if (accountInfo !== undefined) {
        setAccount(accountInfo)
      }
    },
    [applyProfileState, setAccount],
  )

  const loadProfile = useCallback(async () => {
    if (!token) return
    setProfileLoading(true)
    try {
      const { profile: incoming, account: accountInfo } = await requestProfileSnapshot({
        authHeaders,
        t,
      })
      applyProfileState(incoming)
      if (accountInfo) {
        setAccount(accountInfo)
      }
    } catch (error) {
      if (error instanceof BackofficeApiError && error.status === 401) return
      toast.error(error instanceof Error ? error.message : t('profile.toast.loadError'))
    } finally {
      setProfileLoading(false)
    }
  }, [applyProfileState, authHeaders, setAccount, t, token])

  const updateProfile = useCallback(
    async (payload: Partial<AdminProfile>) => {
      if (!ensureAuthenticated()) return
      try {
        const data = await requestProfileUpdate({ authHeaders, payload, t })
        const incoming = data.profile ?? payload
        setProfile((current) => mergeWithCurrentProfile(current, incoming))
        if (data.account) {
          setAccount(data.account as AdminAccount)
        }
        toast.success(t('profile.toast.updated'))
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('profile.toast.updateError'))
      }
    },
    [authHeaders, ensureAuthenticated, setAccount, setProfile, t],
  )

  const resetProfile = useCallback(() => {
    setProfile(DEFAULT_PROFILE)
    setAccount(null)
  }, [setAccount, setProfile])

  return {
    profileLoading,
    hydrateProfile,
    loadProfile,
    updateProfile,
    resetProfile,
  }
}
